<template>
  <WdsStack justify-space-between class="tw-mb-7">
    <WdsHeading as="h1" type="display">
      {{ $t('API Clients') }}
    </WdsHeading>

    <WdsStack align-center gap-sm>
      <WdsButton primary data-test="api-client-management-create-button" @click="createModal.open">
        {{ $t('Create client') }}
      </WdsButton>
    </WdsStack>
  </WdsStack>

  <WdsStack>
    <WdsText>
      {{
        $t(
          'Use the industry standard OAuth 2.0 to authenticate requests to the Worksome API on behalf of your users.',
        )
      }}
    </WdsText>
  </WdsStack>

  <WdsAlert
    v-if="createdCredentials.id"
    :title="$t('Created client credentials')"
    class="tw-my-4"
    closable
    @close="clearCreatedCredentials"
  >
    <template #description>
      <WdsText class="tw-mb-4">
        {{ $t('Please note, the client secret will only be shown once.') }}
      </WdsText>
      <WdsInput
        :model-value="createdCredentials.id"
        :label="$t('Client ID')"
        readonly
        @click="(event) => (event.target as HTMLInputElement).select()"
      />
      <div class="tw-mb-4"></div>
      <WdsInput
        :model-value="createdCredentials.secret"
        :label="$t('Client secret')"
        readonly
        @click="(event) => (event.target as HTMLInputElement).select()"
      />
    </template>
  </WdsAlert>

  <WdsAlert
    v-if="revokedClientName"
    :title="$t('Revoked client')"
    class="tw-my-4"
    closable
    @close="revokedClientName = ''"
  >
    <template #description>
      <WdsText class="tw-mb-4">
        {{ $t('Successfully revoked client ":name".', { name: revokedClientName }) }}
      </WdsText>
    </template>
  </WdsAlert>

  <div class="tw-overflow-x-auto tw-mt-8">
    <div class="tw-inline-block tw-min-w-full tw-py-2 tw-align-middle">
      <div class="tw-border tw-border-gray-200 tw-rounded">
        <table class="tw-min-w-full tw-divide-y tw-divide-gray-300">
          <thead class="tw-bg-gray-50">
            <tr>
              <th
                scope="col"
                class="tw-py-3.5 tw-pl-4 tw-pr-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600 sm:tw-pl-6"
              >
                {{ $t('Name') }}
              </th>
              <th
                scope="col"
                class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600"
              >
                {{ $t('Client ID') }}
              </th>
              <th
                scope="col"
                class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600"
              >
                {{ $t('Created At') }}
              </th>
              <th scope="col" class="tw-relative tw-py-3.5 tw-pl-3 tw-pr-4 sm:tw-pr-6">
                <span class="tw-sr-only">{{ $t('Actions') }}</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="clients.length > 0" class="tw-divide-y tw-divide-gray-200 tw-bg-white">
            <tr v-for="client in clients" :key="client.id">
              <td
                class="tw-whitespace-nowrap tw-py-4 tw-pl-4 tw-pr-3 tw-text-sm tw-font-medium tw-text-gray-900 sm:tw-pl-6"
              >
                {{ client.name }}
              </td>
              <td class="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500">
                {{ client.id }}
              </td>
              <td
                class="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500 tw-font-mono"
              >
                {{ formatDate(new Date(client.created_at), 'YYYY-MM-DD HH:mm') }}
              </td>
              <td
                class="tw-relative tw-whitespace-nowrap tw-py-4 tw-pl-3 tw-pr-4 tw-text-right tw-text-sm tw-font-medium sm:tw-pr-6"
              >
                <RevokeClientButton :client="client" @revoked="handleClientRevoked" />
              </td>
            </tr>
          </tbody>
          <tbody v-else class="tw-divide-y tw-divide-gray-200 tw-bg-white">
            <tr class="tw-text-center">
              <td class="tw-py-2" colspan="4">
                <div class="tw-justify-center">{{ $t('No API clients were found.') }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <WdsModal
    :title="$t('Create API client')"
    :show="createModal.isVisible.value"
    data-test="api-client-management-create-modal"
    @cancel="createModal.close"
  >
    <BaseHtmlForm
      action="/clients"
      method="POST"
      novalidate
      @form-submitted.prevent="handleCreateClient"
    >
      <slot name="inputs-hidden" />

      <WdsInput
        v-model="formState.name"
        :error="getFieldError('name')"
        :label="$t('Client name')"
        lg
        name="name"
        :placeholder="$t('Enter a name for your OAuth2 client')"
        autocomplete="off"
        required
        type="text"
        data-test="api-client-management-name-input"
        @blur="nameTouch"
      />

      <div class="!tw-mt-6">
        <WdsInput
          v-model="formState.redirect"
          :error="getFieldError('redirect')"
          :label="$t('Redirect URL')"
          lg
          name="redirect"
          :placeholder="$t('Enter a redirect URL for your OAuth2 client')"
          autocomplete="off"
          required
          type="text"
          data-test="api-client-management-redirect-input"
          @blur="redirectTouch"
        />
      </div>

      <WdsButton
        class="!tw-mt-[24px] tw-no-spinner"
        :disabled="error"
        full-width
        lg
        primary
        type="submit"
        data-test="api-client-management-submit"
        @click="touchAll"
      >
        {{ $t('Create') }}
      </WdsButton>
    </BaseHtmlForm>
  </WdsModal>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsHeading,
  WdsInput,
  WdsModal,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { helpers, required, url } from '@vuelidate/validators'
import { formatDate } from '@vueuse/core'
import type { ValidationErrors } from './types'
import { useOAuthClients, useModal } from './composables'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import RevokeClientButton from '@/components/api/clients/RevokeClientButton.vue'
import { $t, $t as trans } from '@/composables/translation'
import useCustomVuelidate from '@/composables/useCustomVuelidate'

interface Props {
  errors: ValidationErrors
}

const props = defineProps<Props>()

// API composable
const { clients, fetchClients } = useOAuthClients()

// Modal state
const createModal = useModal()

// Form state
const formState = reactive({
  name: '',
  redirect: '',
})

// Created credentials display
const createdCredentials = ref({ id: '', secret: '' })
const revokedClientName = ref('')

// Validation with useCustomVuelidate
const validationRules = {
  name: {
    required: helpers.withMessage(trans('Please enter a client name'), required),
  },
  redirect: {
    required: helpers.withMessage(trans('Please enter a redirect URL'), required),
    url: helpers.withMessage(trans('A valid URL is required'), url),
  },
}

const {
  name: nameError,
  nameTouch,
  redirect: redirectError,
  redirectTouch,
  touchAll,
  resetAll,
  error,
} = useCustomVuelidate(validationRules, formState)

// Combine validation errors with backend errors
function getFieldError(field: 'name' | 'redirect'): string | undefined {
  // Check backend errors first
  if (Object.prototype.hasOwnProperty.call(props.errors, field)) {
    return props.errors[field][0]
  }

  // Then check validation errors
  if (field === 'name') return nameError.value
  if (field === 'redirect') return redirectError.value

  return undefined
}

// Handlers
async function handleCreateClient(): Promise<void> {
  touchAll()
  if (error.value) return

  const response = await (
    await import('axios')
  ).default.post('/oauth/clients', {
    name: formState.name,
    redirect: formState.redirect,
  })

  // Store credentials for display
  createdCredentials.value = {
    id: response.data.id,
    secret: response.data.plain_secret ?? '',
  }

  // Reset form and close modal
  formState.name = ''
  formState.redirect = ''
  resetAll()
  createModal.close()

  // Refresh list
  await fetchClients()
}

function handleClientRevoked(clientName: string): void {
  revokedClientName.value = clientName
  fetchClients()
}

function clearCreatedCredentials(): void {
  createdCredentials.value = { id: '', secret: '' }
}

// Lifecycle
onMounted(() => {
  fetchClients()
})
</script>
